import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiArrowRight, FiUsers, FiAward } from 'react-icons/fi';

const Hero = () => {
  return (
    <section id="home" className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-white bg-grid-pattern">
      <div className="absolute top-0 right-0 w-[40rem] h-[40rem] bg-brand-purple/5 rounded-full blur-[120px]" />
      <div className="absolute bottom-0 left-0 w-[30rem] h-[30rem] bg-gray-100 rounded-full blur-[100px]" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="inline-flex items-center space-x-2 bg-white text-brand-black px-4 py-2 rounded-full font-bold text-xs tracking-widest mb-8 border border-gray-200 shadow-sm">
              <span className="w-2 h-2 rounded-full bg-brand-purple animate-pulse"></span>
              <span>GiANT™ CERTIFIED GUIDE</span>
            </div>
            
            <h1 className="text-6xl lg:text-7xl font-black text-brand-black leading-[1.1] mb-8 tracking-tight">
              Lead with <span className="text-brand-purple">Confidence.</span><br />
              Grow with <span className="text-brand-purple">Intention.</span>
            </h1>
            
            <p className="text-xl text-gray-500 mb-12 max-w-lg leading-relaxed font-medium">
              Leadership isn’t just a title—it’s how people experience you every day. We help you become the leader people are proud to follow: clear, confident, and genuinely human.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-5">
              <Link to="/contact">
                <button className="bg-brand-purple text-white px-10 py-5 rounded-xl font-bold text-lg hover:bg-purple-700 hover:shadow-glow transition-all flex items-center justify-center group w-full sm:w-auto">
                  Free Intro Call <SafeIcon icon={FiArrowRight} className="ml-3 group-hover:translate-x-1 transition-transform" />
                </button>
              </Link>
              <Link to="/services">
                <button className="bg-white text-brand-black border border-gray-200 px-10 py-5 rounded-xl font-bold text-lg hover:border-brand-black transition-all w-full sm:w-auto">
                  Explore Coaching
                </button>
              </Link>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
            className="relative perspective-1000 hidden lg:block"
          >
            <div className="relative preserve-3d group">
              <div className="relative rounded-[2.5rem] overflow-hidden shadow-2xl shadow-brand-black/20 border-8 border-white bg-white">
                <img 
                  src="https://i.postimg.cc/X7jQqDsS/rs-w-984-h-984.webp" 
                  alt="Leadership Coaching" 
                  className="w-full h-[600px] object-cover hover:scale-105 transition-transform duration-700 ease-out" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-black/40 to-transparent"></div>
              </div>

              {/* Floating Badge 1 - Updated to White Background */}
              <motion.div 
                animate={{ y: [0, -15, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                className="absolute top-12 -left-12 bg-white p-6 rounded-2xl shadow-premium border border-gray-100"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-brand-black rounded-lg flex items-center justify-center text-white">
                    <SafeIcon icon={FiUsers} className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="text-2xl font-black text-brand-black">5 Voices™</div>
                    <div className="text-xs font-bold text-brand-purple uppercase tracking-wider">Certified Guide</div>
                  </div>
                </div>
              </motion.div>

              {/* Floating Badge 2 */}
              <motion.div 
                animate={{ y: [0, 15, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                className="absolute bottom-12 -right-12 bg-brand-black p-6 rounded-2xl shadow-2xl text-white"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-brand-purple rounded-lg flex items-center justify-center">
                    <SafeIcon icon={FiAward} className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-lg font-bold">Transformation</div>
                    <div className="text-xs text-gray-400 font-medium">Over Information</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;